export default {
	process: {
		create: "New process",
		edit: "Edit process",
		explicitLayout: "Show only the panels this process emits",
		explicitLayoutHint:
			"Off, the Operator always shows the Graphly diagram, the Lumina view and the question list, as it did before panels. On, it shows only the panels the process emits — Graphly, Lumina, Question list and Suggestion pool come from the palette — and a process that emits none shows an empty Operator.",
		deleteConfirm: "Delete process “{name}”?",
		name: "Process name",
		search: "Search processes…",
		noResults: "No processes match your search.",
		empty: {
			title: "No processes yet",
			hint: "Create a process to model an operational workflow.",
		},
		loadFailed: {
			title: "This process couldn't be opened",
			hint: "It may have been deleted, or you may not have access to it.",
			back: "Back to processes",
		},
		// What this process may do to the other processes of its dataspace (ProcessGrantsEditor).
		grants: {
			title: "Access to other processes",
			hint: "What this process may do to other processes while it runs, set separately for each process definition. It can always stop, restart, archive or delete itself. A running process reads this when it is launched, so a change reaches the instances launched after the project is built.",
			own: "Other instances of {label}",
			missing: "A removed process ({id})",
			selectAll: "Select all",
			badge: "{n} access | {n} accesses",
			badgeTitle: "May act on {n} process definition | May act on {n} process definitions",
			operations: {
				spawn: "Start",
				send_event: "Send events",
				list: "List",
				stop: "Stop",
				restart: "Restart",
				archive: "Archive",
				delete: "Delete",
			},
			operationHints: {
				spawn: "Start new instances.",
				send_event: "Send an event to an instance, or to every running one.",
				list: "See which instances there are and their state.",
				stop: "Stop an instance.",
				restart: "Restart an instance.",
				archive: "Archive an instance, and bring it back.",
				delete: "Delete an instance.",
			},
		},
		sidebar: {
			/* Accessible name for the tab strip, which HabTabs requires. */
			tabsLabel: "Dataobject sections",
			kindRoot: "Root",
			kindRelated: "Related",
			readOnlyHint: "You can view this process but not change it.",
			attachHint: "To attach a dataobject, {key}-drag this one onto it on the canvas.",
			attachTo: "Attach to",
			attachSearch: "Search {target} in this process…",
			attachNoCandidates: "No eligible dataobjects in this process.",
			// Creating straight into a relation. The label names the target as the
			// heading above does; "New {target}" needs no article in English. The hint
			// says both halves, since the new one is also a dataobject on the canvas.
			createNew: "New {target}",
			createNewHint: "Adds it to this process and attaches it here.",
			createdTag: "New",
			// Naming is offered, never asked for: a new one is called what the model
			// calls it until someone deliberately types a name.
			createdNameIt: "Name it",
			createdPlaced: "Placed beside this dataobject on the canvas.",
			// A relation holds one creator, so filling a filled one is a swap. What it swaps
			// out stays in the process, which the sentence says because a detach is easy to
			// mistake for a delete.
			replace: "Replace",
			replaceWith: "Replace with",
			replaceCancel: "Cancel",
			replaceKeeps: "{name} stays in the process, unattached.",
			createReplaceHint: "Adds one to this process and puts it here instead.",
			sharedShow: "Show it",
			attachNoRelation: "{source} has no relation to {target}.",
			attachCycle:
				"{source} already sits beneath {target}, so attaching it would make a circle.",
			// Names both halves: a pdo has one parent, so attaching it here detached it
			// there, and the edge it lost may be off-screen.
			attachMoved: "Detached {name} from {from} and attached it to {to}.",
			attachAlready: "Already attached here",
			// A filled relation offered by the canvas drag: picking it swaps the creator.
			attachReplaces: "Replaces {name}",
			// The one refusal a drag has left: a filled relation is offered as a swap, so
			// only a drop that is already where it was aimed gets nothing to pick.
			attachAlreadyThere: "{name} is already attached to {parent}.",
			detach: "Detach",
			summary: "Summary",
			relationsAttached: "Relations attached",
			activitiesAttached: "Activities attached",
			// Says "including sections that are closed" because the denominator counts the
			// whole tree: a reader who adds up the visible sections gets a smaller number
			// and files a bug.
			activitiesAttachedHint:
				"{attached} attached of {available} defined across this dataobject and everything embedded in it — including sections that are closed.",
			about: "About this process",
			noDescription: "No description",
			dataobjects: "Dataobjects",
			inProcess: "In this process",
			availableToAdd: "Available to add",
			// What an available dataobject brings, before it is added: its own activities, and the
			// dataobjects already in the process that it shares a relation with, either way round.
			activityCount: "no activities defined | {count} activity | {count} activities",
			relatesTo: "relates to {names}",
			placedHidden:
				"Hidden by the search or filter | All {count} hidden by the search or filter",
			search: "Search dataobjects…",
			noMatches: "No matches",
			term: {
				renamed: "Name diverges from the information model, which calls this “{term}”",
				inherited:
					"Currently uses the name from the information model. A name set here applies to this process dataobject only.",
				modelTerm: "In the information model: “{term}”",
				reset: "Use the model name",
			},
			filter: {
				all: "All dataobjects",
				incomplete: "Incomplete only",
				hidden: "Incomplete only · none hidden | Incomplete only · {count} hidden | Incomplete only · {count} hidden",
				showAll: "Show all",
			},
			sort: {
				title: "Sort by",
				attention: "Attention",
				// "Available to add" only: with activities first, then related to what is placed.
				suggested: "Suggested",
				alphabetical: "A–Z",
				added: "Order added",
			},
		},
		dataobjects: {
			pick: "Add dataobject to process",
			addTitle: "Add a dataobject",
			remove: "Remove from process",
			removeConfirm: "Remove “{name}” from this process? This cannot be undone.",
			removeConfirmAttached:
				"Remove “{name}” and the {count} dataobject attached beneath it? This cannot be undone. | Remove “{name}” and the {count} dataobjects attached beneath it? This cannot be undone.",
			empty: {
				title: "No dataobjects in this process yet",
				hint: "Double-click the canvas to add a root dataobject, or use the button below.",
			},
			noneAvailable: "No dataobjects available to add.",
		},
		activities: {
			attached: "Attached",
			available: "Available",
			attachAll: "Attach all",
			detachAll: "Detach all",
			actionCount: "{count} action | {count} actions",
			noActions: "no actions yet",
			open: "Open activity",
			openReadOnly: "Open activity (read-only)",
			search: "Search activities…",
			noneForDataobject: "No activities defined for this dataobject.",
			noneAttached: "nothing attached",
			noneDefined: "no activities defined",
			// The row states the gap; these say what would close it. Two states, two
			// different weights — one is an omission you fix here (a warning), the other is
			// information: a dataobject may legitimately have no activities at all.
			noneAttachedHint:
				"{count} activity is defined for {dataobject} but none runs here. Open this dataobject to attach one. | {count} activities are defined for {dataobject} but none runs here. Open this dataobject to attach one.",
			noneDefinedHint:
				"No activities are defined for {dataobject}. Define one in the activity editor if something should run here.",
			// Not a gap: something runs here, just against a dataobject embedded in this
			// one rather than against this one. Without the line the row says nothing at
			// all, which reads as "nothing here" — the state it is specifically not in.
			embeddedOnly:
				"{count} runs on an embedded dataobject | {count} run on embedded dataobjects",
			attachedFraction: "{attached} of {available} activities attached",
			// The containment tree. `each` marks an array hop and governs everything to
			// its right — the activity runs once per item, which the dotted path hid.
			// `perJoin` reads the trail leaf-first, so "Tax detail" + " of each " +
			// "Cover line" is the order the sentence needs.
			each: "each",
			perJoin: " of each ",
			runsOncePer: "Runs once per {place}",
			thisDataobject: "this dataobject",
			// Distinct from `noneDefined` on purpose: this sits where a fraction would,
			// so it has to be as short as the number it replaces.
			sectionNoneDefined: "none defined",
			// Not "no actions": the activity is gone, so the count is unknown, not zero.
			deleted: "activity deleted · actions unknown",
		},
		cardinality: {
			attached: "Attached",
			// What the range governs: how many instances the engine makes from the one
			// attached creator at runtime — not how many may be attached here.
			allowed: "Created at runtime",
			capacityHint: "Created at runtime: {allowed}.",
			// The flag, and what would clear it. Only an empty relation that something
			// will need is flagged — a lower bound, or any stored range — since an empty
			// one nobody set a range on is no problem yet.
			//
			// Neither names the target: the row's heading is the target's name, a line
			// above, and naming it costs an article English gets wrong ("a Invoice") and
			// German cannot choose without the term's gender.
			needsOne: "needs one attached",
			emptyHint:
				"Nothing attached, so nothing can be created or linked here at runtime. Attach one below, or {key}-drag this dataobject onto one on the canvas.",
			// Under the creator: why there is only ever one.
			slotHint: "Everything this relation gets at runtime is made from this one.",
			// On an empty relation the range waits for its creator: saved first, it makes the
			// generator add a placeholder at the canvas origin.
			lockedHint: "Attach one first. The range says how many are made from it.",
			// The engine keys a creator by the parent's type, so same-typed pdos share one.
			sharedFlag: "shared at runtime",
			sharedHint:
				"Another dataobject of this kind in the process has its own attached here. At runtime only one of the two is used.",
			inheritedHint:
				"Another dataobject of this kind in the process has one attached here. At runtime it is used for this one too.",
			// The range in words. The four canonical ones get their own phrases rather
			// than a count, because a language may decline "one" differently from "2".
			meaning: {
				any: "any number",
				oneOrMore: "one or more",
				atLeast: "{count} or more",
				exactlyOne: "exactly one",
				exactly: "exactly {count}",
				atMostOne: "at most one",
				atMost: "at most {count}",
				between: "between {min} and {max}",
			},
			rangeInvalid: "Use whole numbers, or * for unbounded.",
			min: "Min",
			max: "Max",
			// The glyph here has to be the one `parseMax` accepts and `formatMax` writes
			// back into the field — a typographic ∗ told people to type a character the
			// parser rejects.
			unboundedHint: "Use * for unbounded.",
			maxInvalid: "Max must be * (unbounded) or ≥ min.",
			noRelations: "This dataobject has no relations to choose from.",
			noRelationsHint: "Define relations in the information model first.",
		},
	},
};
